import { useCallback, useEffect, useRef, useState } from 'react';
import { getBillingRefusalMessage, createIdempotencyKey, useAdminApi } from '../../lib/adminApi';
import type {
  AdminApiError,
  CheckoutOperation,
  CreateCheckoutResponse,
  CreateLaunchRequest,
  CreateLaunchResponse,
  LaunchDetail,
  PaginatedLaunchList,
  RotateAccessCodeResponse,
} from './types';
import { LaunchList } from './LaunchList';
import { LaunchDetail as LaunchDetailView } from './LaunchDetail';
import { LaunchCreateForm } from './LaunchCreateForm';
import { OneTimeLinks } from './OneTimeLinks';
import { RotateAccessCodeResult } from './RotateAccessCodeResult';
import { LaunchOverview } from './LaunchOverview';
import { isAdminApiError, sanitizeDisplayMessage } from '../../lib/adminApi';
import {
  canApproveLaunch,
  canCreateCheckout,
  canDisableAccessCode,
  canEnableAccessCode,
  canRegenerateCheckout,
  canRotateAccessCode,
} from './status';
import type { ClinicPortalWorkspace } from '../../lib/routes';

const LIMIT = 50;
const DISMISS_CURRENT_ONE_TIME_RESULT = 'Dismiss current one-time result first.';

interface ClinicLaunchPanelProps {
  workspaceMode?: ClinicPortalWorkspace;
}

interface CheckoutPanelState {
  result: CreateCheckoutResponse;
  operation: CheckoutOperation;
  origin: { launchId: string; clinicName: string; clinicSlug: string; contactEmail: string };
  recoveryMessage?: string;
}

interface RotatePanelState {
  result: RotateAccessCodeResponse;
  origin: { launchId: string; clinicName: string; clinicSlug: string };
  recoveryMessage?: string;
}

export function ClinicLaunchPanel({ workspaceMode = 'existing' }: ClinicLaunchPanelProps) {
  const api = useAdminApi();
  const [list, setList] = useState<PaginatedLaunchList | null>(null);
  // Tracks whether we've ever had a successful list response. Once true, it
  // stays true across refresh failures and selection switches — even with
  // stale data, last-known-good list items should still render rather than
  // collapse into a misleading "No demo clinics yet" empty state alongside
  // the error banner. The empty-state placeholder is only rendered before
  // the first load OR when list was last known to be empty (R-fix:
  // empty-state collision with list-fetch error banner).
  const [listEverLoaded, setListEverLoaded] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const selectedIdRef = useRef<string | null>(null);
  const [detail, setDetail] = useState<LaunchDetail | null>(null);
  const [listError, setListError] = useState<AdminApiError | null>(null);
  const [error, setError] = useState<AdminApiError | null>(null);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [createResult, setCreateResult] = useState<CreateLaunchResponse | null>(null);
  const [checkoutPanel, setCheckoutPanel] = useState<CheckoutPanelState | null>(null);
  const [checkoutRecoveryError, setCheckoutRecoveryError] = useState<AdminApiError | null>(null);
  const [checkoutPending, setCheckoutPending] = useState(false);
  const [rotatePanel, setRotatePanel] = useState<RotatePanelState | null>(null);
  const [rotateRecoveryError, setRotateRecoveryError] = useState<AdminApiError | null>(null);
  const [rotatePending, setRotatePending] = useState(false);
  const [disablePending, setDisablePending] = useState(false);
  const [enablePending, setEnablePending] = useState(false);
  const [offset, setOffset] = useState(0);
  const createKeyRef = useRef<string | null>(null);
  const workspaceModeRef = useRef<ClinicPortalWorkspace>(workspaceMode);
  const lastCreateRequestRef = useRef<CreateLaunchRequest | null>(null);
  const approveKeyRef = useRef<string | null>(null);
  const lastApproveRef = useRef<{ launchId: string; reason: string } | null>(null);
  const checkoutKeyMapRef = useRef<Map<string, string>>(new Map());
  const checkoutCreationRef = useRef<number>(0);
  const checkoutInFlightRef = useRef(false);
  const rotateKeyMapRef = useRef<Map<string, string>>(new Map());
  const rotateAttemptRef = useRef<number>(0);
  const disableKeyMapRef = useRef<Map<string, string>>(new Map());
  const disableAttemptRef = useRef<number>(0);
  const enableKeyMapRef = useRef<Map<string, string>>(new Map());
  const enableAttemptRef = useRef<number>(0);
  const detailRequestIdRef = useRef(0);
  const rotateInFlightOriginRef = useRef<{ launchId: string } | null>(null);

  const loadList = useCallback(
    // `silent` suppresses this call's own list error on failure. Used by
    // fire-and-forget recovery refreshes issued from a mutation's catch
    // block, which have already set a specific, typed error message —
    // without `silent`, a later failure of this background refresh would
    // overwrite that message with a generic one (REL-2).
    async (currentOffset: number, { skipErrorClear = false, silent = false } = {}) => {
      setLoading(true);
      if (!skipErrorClear) setListError(null);
      try {
        const data = await api.listLaunches({ limit: LIMIT, offset: currentOffset });
        setList(data);
        // Once we've had at least one successful response, render the list
        // table (and its empty-state placeholder, if data.total === 0)
        // rather than collapsing entirely on a later transient fetch
        // failure (R-fix: empty-state collision with list-fetch error).
        setListEverLoaded(true);
      } catch (err) {
        if (!silent) {
          setListError(isAdminApiError(err) ? err : { type: 'unknown', message: 'Unexpected error', recoverable: true });
        }
      } finally {
        setLoading(false);
      }
    },
    [api],
  );

  const loadDetail = useCallback(
    // See `loadList`'s `silent` doc above — same rationale.
    async (launchId: string, { skipErrorClear = false, silent = false } = {}) => {
      setLoading(true);
      if (!skipErrorClear) setError(null);
      const requestId = (detailRequestIdRef.current += 1);
      try {
        const data = await api.getLaunch(launchId);
        // Only update if the user has not selected a different launch while this
        // request was in flight. Otherwise an out-of-order response can overwrite
        // the current detail with stale data.
        if (requestId === detailRequestIdRef.current) {
          setDetail(data);
        }
      } catch (err) {
        // Ignore errors from stale requests that were superseded by a newer
        // selection. This prevents a slow failed request for launch A from
        // clearing the detail or showing an error for launch B.
        if (requestId !== detailRequestIdRef.current) return;
        const error = isAdminApiError(err) ? err : { type: 'unknown' as const, message: 'Unexpected error', recoverable: true };
        // Only clear the detail on a genuine 404; preserve it for transient
        // network/server errors so the user can retry without seeing "not found".
        if (error.type === 'not-found') {
          setDetail(null);
        }
        if (!silent) {
          setError(error);
        }
      } finally {
        // Keep loading true until the latest in-flight request resolves.
        if (requestId === detailRequestIdRef.current) {
          setLoading(false);
        }
      }
    },
    [api],
  );

  useEffect(() => {
    selectedIdRef.current = selectedId;
  }, [selectedId]);

  useEffect(() => {
    workspaceModeRef.current = workspaceMode;
  }, [workspaceMode]);

  useEffect(() => {
    if (workspaceMode === 'existing') {
      loadList(offset);
    }
  }, [loadList, offset, workspaceMode]);

  useEffect(() => {
    if (workspaceMode === 'existing' && selectedId) {
      loadDetail(selectedId);
    } else if (workspaceMode === 'existing') {
      setDetail(null);
      setError(null);
    }
  }, [loadDetail, selectedId, workspaceMode]);

  useEffect(() => {
    setError(null);
    setListError(null);
    if (workspaceMode !== 'creation') {
      setCreateResult(null);
      createKeyRef.current = null;
    }
  }, [workspaceMode]);

  const hasOneTimeBusyState = Boolean(createResult || creating || checkoutPanel || checkoutPending || rotatePanel || rotatePending);

  const selectLaunch = useCallback(
    (launchId: string) => {
      if (selectedIdRef.current === launchId) {
        void loadDetail(launchId);
        return;
      }
      // Clear stale detail/error from the previous selection and invalidate any
      // in-flight detail request before changing selectedId. Without this,
      // selecting clinic B after clinic A loaded can show A's detail until B's
      // request resolves — a flash of stale data.
      setDetail(null);
      setError(null);
      setLoading(true);
      detailRequestIdRef.current += 1;
      setSelectedId(launchId);
    },
    [loadDetail],
  );

  const handleSelect = (launchId: string) => {
    selectLaunch(launchId);
  };

  const handleCreate = async (request: CreateLaunchRequest) => {
    setCreating(true);
    setError(null);
    setCreateResult(null);
    // Reuse the same idempotency key only when the request body is identical to
    // the previous attempt. Editing the form generates a new key.
    const sameBody =
      lastCreateRequestRef.current &&
      JSON.stringify(lastCreateRequestRef.current) === JSON.stringify(request);
    if (!sameBody) {
      createKeyRef.current = createIdempotencyKey();
    }
    lastCreateRequestRef.current = request;
    if (!createKeyRef.current) {
      createKeyRef.current = createIdempotencyKey();
    }
    try {
      const result = await api.createLaunch(request, createKeyRef.current);
      if (workspaceModeRef.current === 'creation') {
        setCreateResult(result);
        setSelectedId(null);
      } else {
        createKeyRef.current = null;
      }
    } catch (err) {
      const baseErr = isAdminApiError(err) ? err : { type: 'unknown' as const, message: 'Unexpected error', recoverable: true };
      let finalErr = baseErr;
      // Create-specific safe copy: never render raw backend message text for
      // this mutation (R15) — always map known statuses to typed copy, the
      // same status-branching discipline already used by
      // rotate/disable/enable below. A client-side eligibility guard (no
      // `status`) keeps its own safe message untouched.
      const status = baseErr.status;
      const code = baseErr.code;
      const refusalMessage = status === 409 ? getBillingRefusalMessage(code) : undefined;
      if (status === 422 && code === 'clinic_contact_email_required') {
        finalErr = {
          ...baseErr,
          message: 'A contact email is required for this launch. Please enter a valid contact email and try again.',
        };
      } else if (refusalMessage) {
        finalErr = { ...baseErr, message: refusalMessage, recoverable: false };
      } else if (status === 409) {
        finalErr = { ...baseErr, message: 'Launch could not be created because of a conflicting state. Refresh and try again.' };
      } else if (status === 429) {
        finalErr = { ...baseErr, message: 'Too many attempts. Wait a moment before creating another launch.' };
      } else if (status !== undefined && status >= 500) {
        finalErr = { ...baseErr, message: 'Admin API error while creating the launch. Try again shortly.' };
      } else if (status !== undefined) {
        // Any other backend-classified status (e.g. other 422 codes, 403,
        // 404): use safe generic copy rather than raw backend text, which
        // classifyError can otherwise embed.
        finalErr = { ...baseErr, message: 'Request rejected while creating the launch. Check the submitted fields and try again.' };
      }
      // status === undefined covers timeout/network errors and the local
      // eligibility guard, whose messages are already safe copy, not raw
      // backend text — leave as-is.
      setError(finalErr);
    } finally {
      setCreating(false);
    }
  };

  const handleApprove = async (reason: string) => {
    if (hasOneTimeBusyState) {
      setError({ type: 'unknown', message: DISMISS_CURRENT_ONE_TIME_RESULT, recoverable: true });
      return;
    }
    const launchId = selectedId;
    if (!launchId) return;
    setLoading(true);
    setError(null);
    const requestId = (detailRequestIdRef.current += 1);
    try {
      // Refresh the latest detail before approving to avoid stale-state approvals.
      const latest = await api.getLaunch(launchId);
      // Guard against out-of-order responses: if the user selected another launch
      // while this refresh was in flight, drop this stale result.
      if (requestId !== detailRequestIdRef.current) return;
      setDetail(latest);
      if (!canApproveLaunch(latest)) {
        throw new Error('Launch is no longer ready for approval. Status changed.');
      }
      const sameApproveAttempt =
        lastApproveRef.current &&
        lastApproveRef.current.launchId === launchId &&
        lastApproveRef.current.reason === reason;
      if (!sameApproveAttempt) {
        approveKeyRef.current = createIdempotencyKey();
      }
      lastApproveRef.current = { launchId, reason };
      await api.approveLaunch(launchId, { reason }, approveKeyRef.current!);
      // Only refresh detail and list if the operator has not moved to another
      // launch while the approval was in flight. Use the live ref because the
      // captured selectedId is always equal to launchId for this invocation.
      if (selectedIdRef.current === launchId) {
        await loadDetail(launchId);
        await loadList(offset);
      }
    } catch (err) {
      // Ignore errors from stale approval attempts that were superseded by a
      // newer selection.
      if (requestId !== detailRequestIdRef.current) return;
      const error = err as Error | AdminApiError;
      let finalErr = isAdminApiError(error)
        ? error
        : { type: 'server' as const, message: error.message, recoverable: true };
      // Backend rejects approval for demo launches — refresh durable detail
      // and show non-retryable copy without claiming activation.
      if (finalErr.status === 409 && finalErr.code === 'approval_not_allowed_for_demo') {
        finalErr = {
          ...finalErr,
          message: 'Demo paid activation is driven by checkout and subscription lifecycle evidence. Approval is not available for demo launches.',
          recoverable: false,
        };
      }
      setError(finalErr);
      if (finalErr.status === 409 && finalErr.code === 'approval_not_allowed_for_demo') {
        if (selectedIdRef.current === launchId) {
          loadDetail(launchId, { skipErrorClear: true });
        }
        loadList(offset, { skipErrorClear: true });
      }
    } finally {
      if (requestId === detailRequestIdRef.current) {
        setLoading(false);
      }
    }
  };

  const handleCreateCheckout = async (operation: CheckoutOperation) => {
    if (checkoutInFlightRef.current) return;
    if (hasOneTimeBusyState) {
      setError({ type: 'unknown', message: DISMISS_CURRENT_ONE_TIME_RESULT, recoverable: true });
      return;
    }
    const launchId = selectedId;
    if (!launchId) return;
    checkoutInFlightRef.current = true;
    setCheckoutPending(true);
    setError(null);
    setCheckoutRecoveryError(null);
    const requestId = (detailRequestIdRef.current += 1);
    const creationId = ++checkoutCreationRef.current;
    try {
      // Refetch the latest detail before proceeding to avoid stale-state
      // checkout creation.
      const latest = await api.getLaunch(launchId);
      // Only update the detail panel if the selection hasn't changed since the
      // refetch started. We must still proceed with checkout creation even when
      // the operator switched selection (R10).
      if (requestId === detailRequestIdRef.current) {
        setDetail(latest);
      }
      // Abort if the launch is no longer eligible based on fresh data.
      const paymentEligibility = operation === 'regenerate' ? canRegenerateCheckout(latest) : canCreateCheckout(latest);
      if (!paymentEligibility.allowed) {
        throw new Error(
          operation === 'regenerate'
            ? 'Launch is no longer eligible for payment-link regeneration. Status changed.'
            : 'Launch is no longer eligible for payment link generation. Status changed.',
        );
      }
      const origin = {
        launchId,
        clinicName: latest.name ?? '',
        clinicSlug: latest.slug ?? '',
        contactEmail: latest.contact_email ?? '',
      };
      // Scope payment-link idempotency by launch and operation so an initial
      // generate and an intentional regenerate never reuse the same key.
      const checkoutScope = `${launchId}:${operation}`;
      if (!checkoutKeyMapRef.current.has(checkoutScope)) {
        checkoutKeyMapRef.current.set(checkoutScope, createIdempotencyKey());
      }
      const key = checkoutKeyMapRef.current.get(checkoutScope)!;
      const result = await api.createCheckout(
        launchId,
        key,
        operation === 'regenerate' ? { regenerate: true } : undefined,
      );
      // Set the checkout result whenever this creation attempt is still current,
      // regardless of which launch is now selected. The one-time URL must remain
      // visible for the originating launch (R10).
      if (creationId === checkoutCreationRef.current) {
        setCheckoutPanel({ result, operation, origin });
        setCheckoutRecoveryError(null);
      }
      // Only refresh detail for the checkout launch if it is still selected.
      // Otherwise we would overwrite the currently visible launch's detail.
      if (selectedIdRef.current === launchId) {
        await loadDetail(launchId);
      }
      // Always refresh the list so durable state is current.
      await loadList(offset);
      // On successful checkout, clear the idempotency key so a new explicit
      // attempt generates a fresh key.
      checkoutKeyMapRef.current.delete(checkoutScope);
    } catch (err) {
      // Only surface errors for the current checkout attempt. The creationId
      // check is sufficient — detailRequestIdRef can be incremented by detail
      // loads or approval prefights, but that should not suppress checkout
      // conflict recovery (P1: 409 recovery dropped on selection switch).
      if (creationId !== checkoutCreationRef.current) return;
      const error = err as Error | AdminApiError;
      const baseErr = isAdminApiError(error)
        ? error
        : { type: 'server' as const, message: error.message, recoverable: true };
      // Checkout-specific 409 recovery: explain that the raw URL cannot be
      // recovered and refresh durable detail so the operator can see current state.
      let finalErr = baseErr;
      if (baseErr.status === 409) {
        const code = baseErr.code;
        const refusalMessage = getBillingRefusalMessage(code);
        let msg: string;
        if (refusalMessage) {
          msg = refusalMessage;
        } else if (code === 'checkout_already_created') {
          msg = 'A payment link was already created for this launch. The one-time URL cannot be recovered. Refresh the launch detail to see current state.';
        } else if (code === 'checkout_creation_in_progress') {
          msg = 'Another payment-link request is in progress. Wait a moment and retry.';
        } else if (code === 'admin_mutation_in_progress') {
          msg = 'Another admin mutation is in progress. Wait a moment and retry.';
        } else if (code === 'clinic_contact_email_required') {
          msg = 'A contact email is required before generating a payment link. Update the launch with a contact email and try again.';
        } else {
          msg = 'Launch state changed during payment-link generation. The one-time URL cannot be recovered. Refresh the launch detail to see current state.';
        }
        finalErr = { ...baseErr, message: msg, recoverable: refusalMessage ? false : baseErr.recoverable };
        const refusalRefreshOptions = refusalMessage
          ? { skipErrorClear: true, silent: true }
          : undefined;
        // Durable refresh: reload detail for the originating launch so the
        // operator sees the latest state after a conflict.
        if (selectedIdRef.current === launchId) {
          void loadDetail(launchId, refusalRefreshOptions);
        }
        void loadList(offset, refusalRefreshOptions);
      }
      if (selectedIdRef.current !== launchId) {
        const origin = {
          launchId,
          clinicName: detail?.name ?? '',
          clinicSlug: detail?.slug ?? '',
          contactEmail: detail?.contact_email ?? '',
        };
        setCheckoutPanel({ result: { launch_id: launchId }, operation, origin, recoveryMessage: finalErr.message });
        setCheckoutRecoveryError(finalErr);
      } else {
        setError(finalErr);
      }
    } finally {
      checkoutInFlightRef.current = false;
      if (creationId === checkoutCreationRef.current) {
        setCheckoutPending(false);
      }
    }
  };

  const handleDismissCheckout = () => {
    setCheckoutPanel(null);
    setCheckoutRecoveryError(null);
  };

  const handleRotateAccessCode = async () => {
    if (hasOneTimeBusyState) {
      setError({ type: 'unknown', message: DISMISS_CURRENT_ONE_TIME_RESULT, recoverable: true });
      return;
    }
    const launchId = selectedId;
    if (!launchId) return;
    setRotatePending(true);
    setError(null);
    setRotateRecoveryError(null);
    const requestId = (detailRequestIdRef.current += 1);
    const attemptId = ++rotateAttemptRef.current;
    rotateInFlightOriginRef.current = { launchId };
    try {
      // Refetch the latest detail before proceeding to avoid stale-state rotation.
      const latest = await api.getLaunch(launchId);
      if (attemptId !== rotateAttemptRef.current) return;
      // Only update the detail panel if the selection hasn't changed since the
      // refetch started. We must still proceed with rotation even when the
      // operator switched selection (R10, mirrors checkout).
      if (requestId === detailRequestIdRef.current) {
        setDetail(latest);
      }
      // Abort if the launch is no longer eligible based on fresh data.
      if (!canRotateAccessCode(latest).allowed) {
        throw new Error('Access code rotation is no longer available. Status changed.');
      }
      const origin = {
        launchId,
        clinicName: latest.name ?? '',
        clinicSlug: latest.slug ?? '',
      };
      // Use a per-launch idempotency key so retrying the same launch uses the
      // same key. A different launch gets a different key.
      if (!rotateKeyMapRef.current.has(launchId)) {
        rotateKeyMapRef.current.set(launchId, createIdempotencyKey());
      }
      const key = rotateKeyMapRef.current.get(launchId)!;
      const result = await api.rotateAccessCode(launchId, key);
      // Set the rotate result whenever this attempt is still current,
      // regardless of which launch is now selected. The one-time code must
      // remain visible for the originating launch (R10, U4 TS5).
      if (attemptId === rotateAttemptRef.current) {
        setRotatePanel({ result, origin });
        setRotateRecoveryError(null);
      }
      // Only refresh detail for the rotated launch if it is still selected.
      // Otherwise we would overwrite the currently visible launch's detail.
      if (selectedIdRef.current === launchId) {
        await loadDetail(launchId);
      }
      // Always refresh the list so durable state (e.g. rotation metadata) is
      // current. skipErrorClear so a failed detail refresh above isn't
      // silently wiped if this list refresh succeeds.
      await loadList(offset, { skipErrorClear: true });
      // Any successful response — even one without a returned code (an
      // ambiguous outcome the backend still consumed the idempotency key
      // for) — means the next explicit rotate must be treated as a fresh
      // attempt. Clearing only when `demo_access_code` was present let a
      // "rotate again intentionally" click replay the same cached no-code
      // response forever (High: no-code recovery loop).
      rotateKeyMapRef.current.delete(launchId);
    } catch (err) {
      // Only surface errors for the current rotate attempt.
      if (attemptId !== rotateAttemptRef.current) return;
      const error = err as Error | AdminApiError;
      const baseErr = isAdminApiError(error)
        ? error
        : { type: 'server' as const, message: error.message, recoverable: true };
      let finalErr = baseErr;
      // Rotate-specific safe copy: never render raw backend message text for
      // this mutation (R15) — always map known statuses to typed copy. A
      // client-side eligibility guard (no `status`) keeps its own safe
      // message untouched, same as the checkout handler above.
      const status = baseErr.status;
      const code = baseErr.code;
      if (status === 409) {
        const msg =
          code === 'admin_mutation_in_progress' || code === 'access_code_rotate_in_progress'
            ? 'Another admin mutation is in progress. Wait a moment and retry.'
            : 'Launch state changed during rotation. The new code cannot be recovered from this attempt — refresh the launch detail to see current state.';
        finalErr = { ...baseErr, message: msg };
        if (selectedIdRef.current === launchId) loadDetail(launchId, { silent: true });
        loadList(offset, { silent: true });
      } else if (status === 429) {
        finalErr = { ...baseErr, message: 'Too many failed attempts. Wait for the lockout window to clear before rotating again.' };
        if (selectedIdRef.current === launchId) loadDetail(launchId, { silent: true });
        loadList(offset, { silent: true });
      } else if (status === 404) {
        finalErr = { ...baseErr, message: 'Rotate access code is not available for this launch. Refresh to check current state.' };
        if (selectedIdRef.current === launchId) loadDetail(launchId, { silent: true });
      } else if (status !== undefined && status >= 500) {
        finalErr = { ...baseErr, message: 'Admin API error while rotating the access code. Try again shortly.' };
        if (selectedIdRef.current === launchId) loadDetail(launchId, { silent: true });
        loadList(offset, { silent: true });
      } else if (status !== undefined) {
        // Any other backend-classified status (e.g. 422): use safe generic
        // copy rather than raw backend text, which classifyError can embed.
        finalErr = { ...baseErr, message: 'Request rejected while rotating the access code. Refresh the launch detail and try again.' };
        if (selectedIdRef.current === launchId) loadDetail(launchId, { silent: true });
      }
      // status === undefined covers timeout/network errors and the local
      // eligibility guard, whose messages are already safe copy, not raw
      // backend text — leave as-is.
      if (selectedIdRef.current !== launchId) {
        // The operator switched away from the launch this rotate was for.
        // Route the error into the rotate recovery panel (scoped to the
        // originating launch via the panel's own labels) instead of the
        // global banner, which is always visible regardless of selection
        // and would otherwise mislabel launch A's failure as belonging to
        // whichever launch is now selected. Previously this only applied to
        // 409s; every status can equally be delayed past a selection switch.
        setRotatePanel({
          result: { launch_id: launchId },
          origin: {
            launchId,
            clinicName: detail?.name ?? '',
            clinicSlug: detail?.slug ?? '',
          },
          recoveryMessage: finalErr.message,
        });
        setRotateRecoveryError(finalErr);
      } else {
        setError(finalErr);
      }
    } finally {
      if (attemptId === rotateAttemptRef.current) {
        setRotatePending(false);
      }
      if (rotateInFlightOriginRef.current?.launchId === launchId) {
        rotateInFlightOriginRef.current = null;
      }
    }
  };

  const handleDismissRotate = () => {
    setRotatePanel(null);
    setRotateRecoveryError(null);
    // If a rotate is still in flight when this fires (disable invalidates an
    // in-flight rotate by bumping rotateAttemptRef and calling this — see
    // below), that attempt's own `finally` will no longer match the bumped
    // counter and will skip resetting rotatePending itself. Reset it here
    // directly so the Rotate button doesn't stay disabled forever (Medium:
    // rotate stuck pending after disable wins the race). Harmless no-op when
    // called from the operator's own dismiss click, since rotatePending is
    // already false by the time a result panel exists to dismiss.
    setRotatePending(false);
  };

  const handleDisableAccessCode = async () => {
    const launchId = selectedId;
    if (!launchId) return;
    if (hasOneTimeBusyState) {
      setError({ type: 'unknown', message: DISMISS_CURRENT_ONE_TIME_RESULT, recoverable: true });
      return;
    }
    setDisablePending(true);
    setError(null);
    const requestId = (detailRequestIdRef.current += 1);
    const attemptId = ++disableAttemptRef.current;
    try {
      // Refetch the latest detail before proceeding to avoid stale-state disable.
      const latest = await api.getLaunch(launchId);
      if (requestId === detailRequestIdRef.current) {
        setDetail(latest);
      }
      // Abort if the launch is no longer eligible based on fresh data.
      if (!canDisableAccessCode(latest).allowed) {
        throw new Error('Disable access code is no longer available. Status changed.');
      }
      // Use a per-launch idempotency key so retrying the same launch uses the
      // same key. A different launch gets a different key.
      if (!disableKeyMapRef.current.has(launchId)) {
        disableKeyMapRef.current.set(launchId, createIdempotencyKey());
      }
      const key = disableKeyMapRef.current.get(launchId)!;
      await api.disableAccessCode(launchId, key);
      if (attemptId === disableAttemptRef.current) {
        // Disable cuts off active sessions immediately, which makes any
        // one-time demo access-code panel (rotate's reveal) for this launch
        // stale — including a rotate mutation that is still IN FLIGHT (its
        // an in-flight rotate origin is set before the mutation call
        // resolves). Only clear the panel that actually belongs to the
        // disabled launch — an unrelated launch's in-flight panel must be
        // left alone (U5 TS4). Bumping the attempt counter here (not just
        // clearing the shown panel) is required: without it, a rotate
        // response that resolves AFTER this disable would still pass its
        // own `attemptId === rotateAttemptRef.current` check and silently
        // resurrect the one-time panel, implying the new code is usable when
        // access was just disabled.
        //
        // Checkout is deliberately NOT touched here. checkout_url is an
        // unrelated one-time payment link (KTD3: "checkout_url remains
        // one-time mutation output and must not be folded into the stable
        // demo URL/access-code component state") — disabling demo access
        // has no bearing on an already-created, unrecoverable payment link,
        // and clearing it would destroy something the operator cannot get
        // back.
        if (rotateInFlightOriginRef.current?.launchId === launchId) {
          rotateAttemptRef.current += 1;
          setRotatePending(false);
          rotateInFlightOriginRef.current = null;
        }
        // A disable is a meaningful state transition — any idempotency key
        // left over from a prior failed rotate attempt for this launch must
        // not be reused once access has been disabled and (later) re-enabled;
        // treat the next rotate as a fresh, intentional action.
        rotateKeyMapRef.current.delete(launchId);
      }
      // Only refresh detail for the disabled launch if it is still selected.
      if (selectedIdRef.current === launchId) {
        await loadDetail(launchId);
      }
      // Always refresh the list so durable state is current. skipErrorClear
      // so a failed detail refresh above isn't silently wiped if this list
      // refresh succeeds.
      await loadList(offset, { skipErrorClear: true });
      // On success, clear the idempotency key so a future explicit attempt
      // (e.g. after a later enable) generates a fresh key.
      disableKeyMapRef.current.delete(launchId);
    } catch (err) {
      // Only surface errors for the current disable attempt.
      if (attemptId !== disableAttemptRef.current) return;
      const error = err as Error | AdminApiError;
      const baseErr = isAdminApiError(error)
        ? error
        : { type: 'server' as const, message: error.message, recoverable: true };
      let finalErr = baseErr;
      // Disable-specific safe copy: never render raw backend message text for
      // this mutation (R15) — always map known statuses to typed copy, same
      // status-branching discipline as rotate.
      const status = baseErr.status;
      const code = baseErr.code;
      if (status === 409) {
        const msg =
          code === 'admin_mutation_in_progress'
            ? 'Another admin mutation is in progress. Wait a moment and retry.'
            : 'Launch state changed before disable could complete. Refresh the launch detail to see current state.';
        finalErr = { ...baseErr, message: msg };
        if (selectedIdRef.current === launchId) loadDetail(launchId, { silent: true });
        loadList(offset, { silent: true });
      } else if (status === 429) {
        finalErr = { ...baseErr, message: 'Too many failed attempts. Wait for the lockout window to clear before trying again.' };
        if (selectedIdRef.current === launchId) loadDetail(launchId, { silent: true });
        loadList(offset, { silent: true });
      } else if (status === 403 || status === 404) {
        finalErr = { ...baseErr, message: 'Disable access code is not available for this launch. Refresh to check current state.' };
        if (selectedIdRef.current === launchId) loadDetail(launchId, { silent: true });
      } else if (status !== undefined && status >= 500) {
        finalErr = { ...baseErr, message: 'Admin API error while disabling the access code. Try again shortly.' };
        if (selectedIdRef.current === launchId) loadDetail(launchId, { silent: true });
        loadList(offset, { silent: true });
      } else if (status !== undefined) {
        finalErr = { ...baseErr, message: 'Request rejected while disabling the access code. Refresh the launch detail and try again.' };
        if (selectedIdRef.current === launchId) loadDetail(launchId, { silent: true });
      }
      // status === undefined covers timeout/network errors and the local
      // eligibility guard, whose messages are already safe copy, not raw
      // backend text — leave as-is.
      // Only surface the error on the global banner when the operator is
      // still viewing the launch this disable was for — the banner is
      // always visible regardless of selection and would otherwise mislabel
      // launch A's failure as belonging to whichever launch is now selected.
      // Unlike rotate/checkout, disable never reveals a one-time value, so
      // there is no per-launch recovery panel to route an off-selection
      // error into; the silent background refreshes above already keep
      // durable state correct.
      if (selectedIdRef.current === launchId) {
        setError(finalErr);
      }
    } finally {
      if (attemptId === disableAttemptRef.current) {
        setDisablePending(false);
      }
    }
  };

  const handleEnableAccessCode = async () => {
    if (hasOneTimeBusyState) {
      setError({ type: 'unknown', message: DISMISS_CURRENT_ONE_TIME_RESULT, recoverable: true });
      return;
    }
    const launchId = selectedId;
    if (!launchId) return;
    setEnablePending(true);
    setError(null);
    const requestId = (detailRequestIdRef.current += 1);
    const attemptId = ++enableAttemptRef.current;
    try {
      // Refetch the latest detail before proceeding to avoid stale-state enable.
      const latest = await api.getLaunch(launchId);
      if (requestId === detailRequestIdRef.current) {
        setDetail(latest);
      }
      // Abort if the launch is no longer eligible based on fresh data.
      if (!canEnableAccessCode(latest).allowed) {
        throw new Error('Enable access code is no longer available. Status changed.');
      }
      // Use a per-launch idempotency key so retrying the same launch uses the
      // same key. A different launch gets a different key.
      if (!enableKeyMapRef.current.has(launchId)) {
        enableKeyMapRef.current.set(launchId, createIdempotencyKey());
      }
      const key = enableKeyMapRef.current.get(launchId)!;
      // Enable never returns a raw code (R9/R14) — the mutation result is
      // discarded and durable detail/list are the source of truth for the
      // refreshed enabled state.
      await api.enableAccessCode(launchId, key);
      // Enable is also a meaningful state transition — any idempotency key
      // left over from a prior failed rotate attempt for this launch must
      // not be reused now that access is enabled again; treat the next
      // rotate as a fresh, intentional action (mirrors disable's cleanup).
      rotateKeyMapRef.current.delete(launchId);
      // Only refresh detail for the enabled launch if it is still selected.
      if (selectedIdRef.current === launchId) {
        await loadDetail(launchId);
      }
      // Always refresh the list so durable state is current. skipErrorClear
      // so a failed detail refresh above isn't silently wiped if this list
      // refresh succeeds.
      await loadList(offset, { skipErrorClear: true });
      // On success, clear the idempotency key so a future explicit attempt
      // (e.g. after a later disable) generates a fresh key.
      enableKeyMapRef.current.delete(launchId);
    } catch (err) {
      // Only surface errors for the current enable attempt.
      if (attemptId !== enableAttemptRef.current) return;
      const error = err as Error | AdminApiError;
      const baseErr = isAdminApiError(error)
        ? error
        : { type: 'server' as const, message: error.message, recoverable: true };
      let finalErr = baseErr;
      // Enable-specific safe copy: never render raw backend message text for
      // this mutation (R15) — always map known statuses to typed copy, same
      // status-branching discipline as rotate/disable.
      const status = baseErr.status;
      const code = baseErr.code;
      if (status === 409) {
        const msg =
          code === 'admin_mutation_in_progress'
            ? 'Another admin mutation is in progress. Wait a moment and retry.'
            : 'Launch state changed before enable could complete. Refresh the launch detail to see current state.';
        finalErr = { ...baseErr, message: msg };
        if (selectedIdRef.current === launchId) loadDetail(launchId, { silent: true });
        loadList(offset, { silent: true });
      } else if (status === 429) {
        finalErr = { ...baseErr, message: 'Too many failed attempts. Wait for the lockout window to clear before trying again.' };
        if (selectedIdRef.current === launchId) loadDetail(launchId, { silent: true });
        loadList(offset, { silent: true });
      } else if (status === 403 || status === 404) {
        finalErr = { ...baseErr, message: 'Enable access code is not available for this launch. Refresh to check current state.' };
        if (selectedIdRef.current === launchId) loadDetail(launchId, { silent: true });
      } else if (status !== undefined && status >= 500) {
        finalErr = { ...baseErr, message: 'Admin API error while enabling the access code. Try again shortly.' };
        if (selectedIdRef.current === launchId) loadDetail(launchId, { silent: true });
        loadList(offset, { silent: true });
      } else if (status !== undefined) {
        finalErr = { ...baseErr, message: 'Request rejected while enabling the access code. Refresh the launch detail and try again.' };
        if (selectedIdRef.current === launchId) loadDetail(launchId, { silent: true });
      }
      // status === undefined covers timeout/network errors and the local
      // eligibility guard, whose messages are already safe copy, not raw
      // backend text — leave as-is.
      // Same off-selection scoping as disable above: never surface an
      // enable failure on the global banner for a launch the operator is no
      // longer viewing.
      if (selectedIdRef.current === launchId) {
        setError(finalErr);
      }
    } finally {
      if (attemptId === enableAttemptRef.current) {
        setEnablePending(false);
      }
    }
  };

  const handleBackToList = () => {
    setCreateResult(null);
    createKeyRef.current = null;
  };

  const handleRetry = () => {
    if (workspaceMode === 'creation' && lastCreateRequestRef.current) {
      void handleCreate(lastCreateRequestRef.current);
      return;
    }
    if (selectedId && (error?.type === 'not-found' || error?.type === 'server' || error?.type === 'network')) {
      loadDetail(selectedId);
    } else if (listError) {
      loadList(offset);
    } else {
      loadList(offset);
    }
  };

  const showGlobalError =
    (error && (workspaceMode === 'creation' || !selectedId)) ||
    (listError && listError.recoverable && workspaceMode === 'existing' && !selectedId);
  const globalError = showGlobalError ? (workspaceMode === 'existing' && !selectedId && listError ? listError : error) : null;
  const selectedListError = workspaceMode === 'existing' && selectedId && listError?.recoverable ? listError : null;

  if (workspaceMode === 'creation') {
    return (
      <div className="create-workspace" data-testid="clinic-launch-panel-creation">
        {globalError && (
          <div className="error-banner" role="alert" data-testid="error-banner">
            <strong>
              {globalError.type === 'auth'
                ? 'Authentication error'
                : globalError.type === 'not-found'
                  ? 'Not found'
                  : globalError.type === 'server'
                    ? 'Action failed'
                    : 'Configuration error'}
            </strong>
            <p>{sanitizeDisplayMessage(globalError.message)}</p>
            {globalError.recoverable && <button onClick={handleRetry}>Retry</button>}
          </div>
        )}

        {createResult ? (
          <OneTimeLinks result={createResult} onBack={handleBackToList} />
        ) : (
          <>
            <div className="workspace-summary-grid" data-testid="creation-summary-grid">
              <div className="status-card">
                <h4>Stable clinic URL</h4>
                <p>Reusable after create</p>
              </div>
              <div className="status-card">
                <h4>Access code</h4>
                <p>One-time reveal</p>
              </div>
              <div className="status-card">
                <h4>Payment link</h4>
                <p>Optional one-time reveal</p>
              </div>
            </div>

            <div className="create-workspace-grid">
              <LaunchCreateForm onSubmit={handleCreate} submitting={creating} />
              <aside className="panel workspace-note-card" data-testid="creation-safety-card">
                <h3>Safety</h3>
                <p>
                  <span className="status-tag status-tag--success">Ready</span> Clinic-facing demo pages
                  use the standard CanisX demo presentation.
                </p>
                <p>
                  <span className="status-tag status-tag--warning">One-time</span> Access codes and
                  raw payment links are not durable reads.
                </p>
              </aside>
            </div>
          </>
        )}
      </div>
    );
  }

  return (
    <div className="existing-workspace" data-testid="clinic-launch-panel-existing">
      {globalError && (
        <div className="error-banner" role="alert" data-testid="error-banner">
          <strong>
            {globalError.type === 'auth'
              ? 'Authentication error'
              : globalError.type === 'not-found'
                ? 'Not found'
                : globalError.type === 'server'
                  ? 'Action failed'
                  : 'Configuration error'}
          </strong>
          <p>{sanitizeDisplayMessage(globalError.message)}</p>
          {globalError.recoverable && <button onClick={handleRetry}>Retry</button>}
        </div>
      )}

      <div className="workspace-toolbar">
        <button className="btn btn--secondary" onClick={() => loadList(offset)} disabled={loading} data-testid="refresh-list">
          Refresh
        </button>
      </div>

      {selectedListError && (
        <div className="error-banner" role="alert" data-testid="list-error-banner">
          <strong>List refresh failed</strong>
          <p>{sanitizeDisplayMessage(selectedListError.message)}</p>
          <button onClick={() => loadList(offset)}>Retry</button>
        </div>
      )}

      <div className="workspace">
        <div className="existing-workspace-list">
          {/* Only render the list once we've had at least one successful list
              response. Until then, neither the panel chrome nor its empty-state
              placeholder is shown — the global error banner above already tells
              the operator what went wrong, and showing "No demo clinics yet"
              alongside a fetch error is misleading (R-fix: empty-state collision
              with list-fetch error banner). */}
          {listEverLoaded ? (
            <LaunchList
              items={list?.items ?? []}
              total={list?.total ?? 0}
              limit={LIMIT}
              offset={offset}
              selectedId={selectedId}
              loading={loading}
              onSelect={handleSelect}
              onPage={setOffset}
            />
          ) : loading ? (
            <div className="panel" data-testid="launch-list-pending">
              <div className="panel-header">
                <div>
                  <p className="portal-eyebrow">Existing Clinic Demos</p>
                  <h3>Demo clinics</h3>
                </div>
              </div>
              <div className="loading">Loading…</div>
            </div>
          ) : // !listEverLoaded && !loading && error → render nothing (the
            // global error banner above is the sole feedback), so the empty
            // state cannot cosplay as "no clinics" alongside the error.
            null}
        </div>
        <div className="existing-workspace-detail">
          {checkoutPanel && (
            <OneTimeLinks
              result={checkoutPanel.result}
              onBack={handleDismissCheckout}
              checkoutOnly
              checkoutOperation={checkoutPanel.operation}
              clinicName={checkoutPanel.origin.clinicName}
              clinicSlug={checkoutPanel.origin.clinicSlug}
              contactEmail={checkoutPanel.origin.contactEmail}
              onViewLaunch={() => {
                  if (checkoutPanel.result.launch_id) {
                  selectLaunch(checkoutPanel.result.launch_id);
                }
              }}
              recoveryMessage={checkoutPanel.recoveryMessage ?? checkoutRecoveryError?.message}
            />
          )}
          {rotatePanel && (
            <RotateAccessCodeResult
              result={rotatePanel.result}
              onBack={handleDismissRotate}
              clinicName={rotatePanel.origin.clinicName}
              clinicSlug={rotatePanel.origin.clinicSlug}
              onViewLaunch={() => {
                if (rotatePanel.result.launch_id) {
                  selectLaunch(rotatePanel.result.launch_id);
                }
              }}
              recoveryMessage={rotatePanel.recoveryMessage ?? rotateRecoveryError?.message}
            />
          )}
          {selectedId ? (
            <LaunchDetailView
              key={selectedId}
              detail={detail}
              loading={loading}
              error={error}
              onApprove={handleApprove}
              onCreateCheckout={handleCreateCheckout}
              checkoutPending={checkoutPending}
              checkoutBlockedMessage={hasOneTimeBusyState ? DISMISS_CURRENT_ONE_TIME_RESULT : undefined}
              onRotateAccessCode={handleRotateAccessCode}
              rotatePending={rotatePending}
              rotateBlockedMessage={hasOneTimeBusyState ? DISMISS_CURRENT_ONE_TIME_RESULT : undefined}
              onDisableAccessCode={handleDisableAccessCode}
              disablePending={disablePending}
              disableBlockedMessage={hasOneTimeBusyState ? DISMISS_CURRENT_ONE_TIME_RESULT : undefined}
              onEnableAccessCode={handleEnableAccessCode}
              enablePending={enablePending}
              onRefresh={() => loadDetail(selectedId)}
              onClose={() => setSelectedId(null)}
            />
          ) : listEverLoaded ? (
            <LaunchOverview
              items={list?.items ?? []}
              total={list?.total ?? 0}
              limit={LIMIT}
              offset={offset}
              loading={loading}
            />
          ) : null}
        </div>
      </div>
    </div>
  );
}
